import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Leaf, Wrench, CalendarCheck, Handshake, Star,
  ChevronDown, Check, MapPin,
} from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import Marquee from "../components/Marquee";
import BeforeAfter from "../components/BeforeAfter";
import CTABanner from "../components/CTABanner";
import { services, advantages, whyChoose, method } from "../data/services";
import { realisations, beforeAfter, heroImage, aboutImage } from "../data/realisations";
import { communeSlug } from "../data/villes";
import { avis, avisNote } from "../data/avis";
import { site } from "../data/site";

const iconMap = { leaf: Leaf, wrench: Wrench, calendar: CalendarCheck, handshake: Handshake };

export default function Home() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const bgScale = useTransform(scrollY, [0, 900], [1.05, 1.2]);

  return (
    <>
      {/* ─── HERO ─── */}
      <section className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img
            src={heroImage}
            alt="Jardin parfaitement entretenu par Embek Group"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/45 to-forest-dark/25" />
        <div className="grain absolute inset-0" />

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col justify-end pb-20 lg:pb-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-10 bg-moss" />
            <span className="eyebrow text-sand/80">Jardinage — Entretien — Aménagement extérieur</span>
          </motion.div>

          <h1 className="mt-6 font-serif font-medium tracking-tight text-sand text-[13vw] sm:text-7xl lg:text-8xl leading-[1.02]">
            <MaskedLine delay={0.35}>Votre extérieur,</MaskedLine>
            <MaskedLine delay={0.5}>
              <em className="italic font-normal">notre savoir-faire.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base sm:text-lg text-sand/80 font-light leading-relaxed"
          >
            {site.baseline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/demander-un-devis" data-testid="hero-devis-cta" className="btn-light">
              Demander un devis <ArrowRight size={15} />
            </Link>
            <a href="#services" data-testid="hero-services-cta" className="btn-outline">
              Découvrir nos services
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 right-6 lg:right-12 hidden sm:flex flex-col items-center gap-2 text-sand/60"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Défiler</span>
          <ChevronDown size={16} className="animate-bounce" />
        </motion.div>
      </section>

      {/* ─── ARGUMENTS ─── */}
      <section className="bg-sand border-b border-forest/10" data-testid="arguments-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-2 lg:grid-cols-4">
          {advantages.map((a, i) => {
            const Icon = iconMap[a.icon];
            return (
              <Reveal key={a.title} delay={i * 0.08} className={`py-8 lg:py-10 px-2 lg:px-8 ${i < 3 ? "lg:border-r lg:border-forest/10" : ""}`}>
                <div className="flex flex-col items-start gap-3">
                  <span className="w-11 h-11 rounded-full bg-forest/8 flex items-center justify-center text-moss">
                    <Icon size={20} />
                  </span>
                  <h3 className="font-semibold text-forest text-[15px]">{a.title}</h3>
                  <p className="text-[13px] leading-relaxed text-coal-muted">{a.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Marquee
        items={[
          "Entretien de jardins", "Taille de haies", "Création de jardins", "Aménagement paysager",
          "Tonte de pelouse", "Nettoyage extérieur", "Vide-cave", "Entretien régulier",
        ]}
      />

      {/* ─── SERVICES ─── */}
      <section id="services" className="py-20 lg:py-32 scroll-mt-16" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <SectionHeading
              eyebrow="Nos services"
              title="Un savoir-faire complet pour vos espaces verts"
              description="De l'entretien régulier à la création de jardins, chaque prestation est réalisée avec soin et précision."
            />
            <Reveal delay={0.15} className="shrink-0">
              <Link to="/services" data-testid="services-see-all-button" className="btn-outline-forest">
                Voir tous nos services <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08} className="h-full">
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group h-full flex flex-col bg-white rounded-3xl overflow-hidden border border-forest/10 shadow-sm hover:shadow-xl hover:shadow-forest/10 transition-all duration-500 hover:-translate-y-1.5"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-sand/90 backdrop-blur text-forest text-[10px] font-semibold uppercase tracking-[0.2em]">
                      0{i + 1}
                    </span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl font-medium text-forest">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-coal-muted flex-1">{s.short}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {s.subs.slice(0, 4).map((sub) => (
                        <span key={sub} className="px-3 py-1 rounded-full bg-sand-mid text-[11px] font-medium text-forest/80">
                          {sub}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── POURQUOI NOUS CHOISIR ─── */}
      <section className="bg-forest-dark grain py-20 lg:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-[1fr_1.25fr] gap-14 lg:gap-20">
          <div className="flex flex-col">
            <SectionHeading
              dark
              eyebrow="Notre engagement"
              title="Pourquoi choisir Embek Group ?"
              description="Parce qu'un beau jardin demande plus que de l'entretien : de l'attention, de la régularité et un vrai sens du détail."
            />
            <Reveal delay={0.2} className="mt-10 flex-1">
              <div className="relative rounded-3xl overflow-hidden h-64 lg:h-full lg:min-h-[300px]">
                <img
                  src="https://images.unsplash.com/photo-1633330948542-0b3bdeefcdb3?crop=entropy&cs=srgb&fm=jpg&q=80"
                  alt="Espace extérieur aménagé"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/60 to-transparent" />
              </div>
            </Reveal>
          </div>
          <div>
            {whyChoose.map((w, i) => (
              <Reveal key={w.num} delay={i * 0.08}>
                <div className="flex gap-7 py-8 border-t border-sand/10 group">
                  <span className="font-serif text-5xl lg:text-6xl font-light text-sand/15 group-hover:text-moss/50 transition-colors duration-500 leading-none">
                    {w.num}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-sand">{w.title}</h3>
                    <p className="mt-2 text-sand/60 font-light leading-relaxed">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── À PROPOS ─── */}
      <section className="py-20 lg:py-32" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal className="relative">
            <div className="rounded-t-[10rem] rounded-b-3xl overflow-hidden h-[420px] lg:h-[540px] shadow-2xl shadow-forest/15">
              <img src={aboutImage} alt="Intervention d'entretien par Embek Group" loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-2 sm:right-8 bg-forest text-sand rounded-2xl px-6 py-5 shadow-xl shadow-forest/25 flex items-center gap-4">
              <span className="w-10 h-10 rounded-full bg-sand/15 flex items-center justify-center">
                <Leaf size={18} />
              </span>
              <div>
                <p className="font-serif text-lg leading-tight">Embek Group</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-sand/60 mt-0.5">Savoir-faire & passion</p>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="À propos"
              title="Embek Group, au service de vos espaces verts"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-coal-muted font-light leading-relaxed">
                Chez Embek Group, nous avons à cœur de transformer, entretenir et valoriser vos espaces extérieurs.
                Notre objectif est de proposer un travail professionnel, soigné et adapté à chaque client.
              </p>
              <p className="mt-4 text-coal-muted font-light leading-relaxed">
                Qu'il s'agisse de l'entretien régulier d'un jardin, de la taille d'une haie, de la création d'un nouvel
                espace vert ou de la remise en état d'un terrain, nous vous accompagnons avec sérieux et savoir-faire.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="mt-7 space-y-3">
                {["Sérieux, ponctualité et qualité de travail", "Prestations adaptées à chaque jardin", "Un interlocuteur attentif, du devis à l'intervention"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm font-medium text-forest">
                    <span className="w-5 h-5 rounded-full bg-moss/15 text-moss flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.25}>
              <Link to="/a-propos" data-testid="about-preview-button" className="btn-outline-forest mt-9">
                Découvrir notre histoire <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── RÉALISATIONS ─── */}
      <section className="bg-sand-mid py-20 lg:py-32" data-testid="realisations-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeading
            align="center"
            eyebrow="Nos réalisations"
            title="Des transformations qui parlent d'elles-mêmes"
            description="Avant / après : glissez le curseur pour découvrir ce qu'un entretien professionnel change à un extérieur."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-8">
            {beforeAfter.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.1}>
                <BeforeAfter before={b.before} after={b.after} title={b.title} category={b.category} />
              </Reveal>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {realisations.slice(0, 4).map((r, i) => (
              <Reveal key={r.id} delay={i * 0.06}>
                <div className="group relative rounded-2xl overflow-hidden aspect-[4/3]" data-testid={`realisation-thumb-${r.id}`}>
                  <img
                    src={r.image}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/70 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-sand/70 font-semibold">{r.category}</span>
                    <p className="text-sm text-sand font-medium mt-0.5">{r.title}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/realisations" data-testid="realisations-see-all-button" className="btn-primary">
              Voir toutes nos réalisations <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ─── MÉTHODE ─── */}
      <section className="py-20 lg:py-32" data-testid="method-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeading
            align="center"
            eyebrow="Notre méthode"
            title="Un processus simple, du premier échange à l'intervention"
          />
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {method.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1} className="relative">
                <div className="lg:border-l lg:border-forest/15 lg:pl-8">
                  <span className="font-serif text-6xl lg:text-7xl font-light text-moss/30 leading-none">{m.num}</span>
                  <h3 className="mt-4 font-serif text-2xl text-forest">{m.title}</h3>
                  <p className="mt-2 text-sm text-coal-muted font-light leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/demander-un-devis" data-testid="method-devis-cta" className="btn-primary">
              Obtenir un devis gratuit <ArrowRight size={15} />
            </Link>
            <Link to="/rendez-vous" data-testid="method-rdv-cta" className="btn-outline-forest">
              Prendre rendez-vous <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ─── AVIS ─── */}
      <section className="bg-sand border-y border-forest/10 py-20 lg:py-32" data-testid="avis-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeading
            align="center"
            eyebrow="Avis clients"
            title="Ils nous font confiance"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {avis.slice(0, 3).map((a, i) => (
              <Reveal key={a.nom} delay={i * 0.08} className="h-full">
                <figure className="h-full flex flex-col bg-white rounded-3xl border border-forest/10 p-8 shadow-sm" data-testid={`avis-card-${i}`}>
                  <div className="flex gap-1 text-[#D9A441]" aria-label="5 étoiles">
                    {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <blockquote className="mt-4 flex-1 font-serif italic text-lg text-forest leading-relaxed">
                    « {a.texte} »
                  </blockquote>
                  <figcaption className="mt-5 text-sm font-semibold text-coal-muted">{a.nom}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6 text-center">
            <p className="text-[11px] text-coal/40 italic">{avisNote}</p>
            <Link to="/avis" data-testid="avis-see-all-button" className="btn-outline-forest mt-6">
              Lire tous les avis <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ─── ZONE D'INTERVENTION ─── */}
      <section className="py-20 lg:py-28" data-testid="zone-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading
              eyebrow="Zone d'intervention"
              title="Nous intervenons dans votre région"
              description="Nos équipes se déplacent autour de Charleroi, Namur et Mons pour l'entretien et l'aménagement de vos espaces verts. Votre commune est dans la liste ? Demandez votre devis ou réservez votre créneau en ligne."
            />
            <Reveal delay={0.2}>
              <div className="mt-8 space-y-6" data-testid="zone-communes">
                {site.zones.map((z) => (
                  <div key={z.name}>
                    <h4 className="flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-moss mb-3">
                      <MapPin size={14} />
                      {z.ville ? (
                        <Link to={`/jardinier-${z.ville}`} data-testid={`zone-link-${z.ville}`} className="hover:text-forest transition-colors underline underline-offset-4 decoration-moss/40">
                          {z.name}
                        </Link>
                      ) : (
                        <span>{z.name}</span>
                      )}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {z.communes.map((c, i) => (
                        <Link
                          key={i}
                          to={`/jardinier-${communeSlug(c)}`}
                          data-testid={`zone-commune-${z.ville || "mons"}-${i}`}
                          className="px-3.5 py-1.5 rounded-full border border-forest/15 bg-white text-[12.5px] font-medium text-forest/80 hover:border-moss/50 hover:text-forest transition-colors"
                        >
                          {c}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="flex flex-col sm:flex-row gap-4 mt-9">
                <Link to="/demander-un-devis" data-testid="zone-contact-button" className="btn-primary">
                  Demander un devis <ArrowRight size={15} />
                </Link>
                <Link to="/rendez-vous" data-testid="zone-rdv-button" className="btn-outline-forest">
                  Prendre rendez-vous <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:sticky lg:top-28">
            <div className="rounded-3xl overflow-hidden border border-forest/10 shadow-lg shadow-forest/10 h-[420px]">
              <iframe
                title="Carte de la zone d'intervention Embek Group"
                src="https://www.google.com/maps?q=Wallonie,+Belgique&z=8&hl=fr&output=embed"
                className="w-full h-full grayscale-[0.3]"
                loading="lazy"
                data-testid="zone-map-iframe"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
